import { Invoice, CompanyInfo, Transaction } from '../types';
import { numberToArabicWords } from './n2a-kuwait';

// تهيئة التواريخ بشكل نظيف لتجنب مشاكل Bidirectional (RTL/LTR)
export const formatDate = (dateValue: any): string => {
  if (!dateValue) return '---';
  try {
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return String(dateValue);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (e) {
    return String(dateValue);
  }
};

const formatDateDDMMYYYY = (dateValue: any): string => {
  if (!dateValue) return '---';
  try {
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return String(dateValue);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (e) {
    return String(dateValue);
  }
};

const formatDateYYYYMMDD = (dateValue: any): string => {
  if (!dateValue) return '---';
  try {
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return String(dateValue);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  } catch (e) {
    return String(dateValue);
  }
};

const getMonthPeriod = (monthDue: string): { start: string; end: string } => {
  if (!monthDue) return { start: '---', end: '---' };
  try {
    let year = new Date().getFullYear();
    let month = new Date().getMonth() + 1;
    
    if (monthDue.includes('-')) {
      const parts = monthDue.split('-');
      year = Number(parts[0]);
      month = Number(parts[1]);
    }
    
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    
    const formatDateSlash = (d: Date) => {
      const day = String(d.getDate()).padStart(2, '0');
      const monthStr = String(d.getMonth() + 1).padStart(2, '0');
      const yearStr = d.getFullYear();
      return `${yearStr}/${monthStr}/${day}`;
    };
    
    return {
      start: formatDateSlash(startDate),
      end: formatDateSlash(endDate)
    };
  } catch (e) {
    return { start: '---', end: '---' };
  }
};

// قالب طباعة المطالبة المالية ووصل الإيجار الموحد المطابق لـ bill.jpg
export const getCollectionNoticeHtml = (
  invoice: Invoice,
  companyInfo: CompanyInfo | null,
  linkedExpenses: Transaction[] = [],
  isPaid: boolean = false
): string => {
  const serviceChargeVal = Number(invoice.serviceCharge || 0);
  const totalAmount = Number(invoice.amount) + serviceChargeVal + linkedExpenses.reduce((sum, exp) => sum + Number(exp.amount), 0);
  const amountWordsAr = numberToArabicWords(totalAmount);

  // حسابات الخصم (الفرق بين القيمة الدفترية والمبلغ الفعلي)
  const bookValue = Number(invoice.contractRent || invoice.amount);
  const actualRent = Number(invoice.amount);
  const discount = bookValue > actualRent ? bookValue - actualRent : 0;

  const docNo = isPaid 
    ? (invoice.paymentId || String(invoice.id).toUpperCase()) 
    : String(invoice.id).toUpperCase();

  const titleAr = isPaid ? 'وصل إيجار' : 'مطالبة مالية بالإيجار';
  const titleEn = isPaid ? 'RENT RECEIPT' : 'RENT CLAIM NOTICE';

  const receiptNoLabelAr = isPaid ? 'رقم الإيصال' : 'رقم المطالبة';

  const contractNo = 1000 + Number(invoice.tenantId || 0);

  const paymentDate = isPaid 
    ? (invoice.paymentDate || invoice.date || new Date()) 
    : (invoice.date || new Date());

  const monthPeriod = getMonthPeriod(invoice.monthDue);

  const formatDateWithTime = (dateValue: any): string => {
    if (!dateValue) return '---';
    try {
      const d = new Date(dateValue);
      if (isNaN(d.getTime())) return String(dateValue);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch (e) {
      return String(dateValue);
    }
  };

  const copyLabel = isPaid ? 'نسخة العميل' : 'مطالبة مالية';

  return `
    <div class="print-wrapper" style="border: 2px solid #000; padding: 10px; background: white; direction: rtl; font-family: 'Inter', 'Noto Sans AR', sans-serif; box-sizing: border-box; width: 100%; max-width: 794px; margin: 0 auto; font-size: 11px;">
      
      <!-- 1. Header (Company details & Logo) -->
      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; align-items: center; border-bottom: 1px solid #000; padding-bottom: 6px; margin-bottom: 8px;">
        <!-- Left (English details) -->
        <div style="text-align: left; font-size: 10px; color: #000; font-family: monospace; line-height: 1.5;" dir="ltr">
          <div style="font-weight: bold; font-size: 12px; margin-bottom: 2px;">${companyInfo?.name_en || 'FLY RENT SYSTEM'}</div>
          <div>C.R. No: ${companyInfo?.cr_number || '---'}</div>
          <div>Tel: ${companyInfo?.phone || '---'}</div>
          <div>Fax: ${companyInfo?.fax || '---'}</div>
          <div>Email: ${companyInfo?.email || '---'}</div>
        </div>

        <!-- Center (Logo) -->
        <div style="display: flex; justify-content: center; align-items: center;">
          ${companyInfo?.logo_url ? `
            <img src="${companyInfo.logo_url}" alt="Logo" style="max-height: 45px; max-width: 100%; object-fit: contain;" />
          ` : `
            <div style="border: 1px solid #000; padding: 4px 8px; border-radius: 6px; color: #000; font-size: 11px; font-weight: bold; text-align: center;">LOGO</div>
          `}
        </div>

        <!-- Right (Arabic details) -->
        <div style="text-align: right; font-size: 10px; color: #000; line-height: 1.5; font-weight: bold;">
          <div style="font-weight: bold; font-size: 12px; margin-bottom: 2px;">${companyInfo?.name_ar || 'نظام فلاي رينت لإدارة العقارات'}</div>
          <div>سجل تجاري: ${companyInfo?.cr_number || '---'}</div>
          <div>تليفون: ${companyInfo?.phone || '---'}</div>
          <div>فاكس: ${companyInfo?.fax || '---'}</div>
          <div>البريد الإلكتروني: ${companyInfo?.email || '---'}</div>
        </div>
      </div>

      <!-- 2. Document Title: "وصل إيجار / RENT RECEIPT" -->
      <div style="display: flex; justify-content: center; margin-bottom: 8px;">
        <div style="border: 1px solid #000; border-radius: 4px; padding: 2px 30px; text-align: center; background-color: #fff;">
          <div style="font-size: 14px; font-weight: bold; color: #000; letter-spacing: 0.5px;">${titleAr}</div>
          <div style="font-size: 10px; font-weight: bold; color: #000; font-family: monospace; text-transform: uppercase; margin-top: 1px; letter-spacing: 1.5px;">${titleEn}</div>
        </div>
      </div>

      <!-- 3. Side-by-Side: Receipt/Contract Numbers (RIGHT) & Amount Box (LEFT) — aligned at bottom -->
      <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 8px; gap: 16px; direction: ltr;">
        
        <!-- Amount Box (LEFT in LTR = visual left) -->
        <div style="width: 28%; border: 1px solid #000; border-radius: 4px; overflow: hidden; text-align: center;">
          <div style="background-color: #e8e8e8; padding: 2px 10px; font-size: 11px; font-weight: bold; border-bottom: 1px solid #000; display: flex; justify-content: space-between;">
            <span dir="ltr">K.D.</span>
            <span dir="rtl">المبلغ</span>
          </div>
          <div style="font-size: 18px; font-weight: bold; color: #000; padding: 4px; font-family: monospace; letter-spacing: 1px;">
            ${totalAmount.toFixed(3)}
          </div>
        </div>
        
        <!-- Receipt & Contract Numbers Box (RIGHT in LTR = visual right) -->
        <div style="width: 40%; border: 1px solid #000; border-radius: 4px; overflow: hidden; font-size: 11px; background-color: #e8e8e8;">
          <!-- Row 1: Receipt No -->
          <div style="display: flex; border-bottom: 1px solid #000; direction: ltr;">
            <div style="width: 55%; padding: 5px 10px; text-align: center; font-weight: bold; font-family: monospace; background-color: #e8e8e8; border-right: 1px solid #000;">
              ${docNo}
            </div>
            <div style="width: 45%; padding: 5px 10px; text-align: right; font-weight: bold; background-color: #e8e8e8; direction: rtl;">
              ${receiptNoLabelAr}
            </div>
          </div>
          <!-- Row 2: Contract No -->
          <div style="display: flex; direction: ltr;">
            <div style="width: 55%; padding: 5px 10px; text-align: center; font-weight: bold; font-family: monospace; background-color: #e8e8e8; border-right: 1px solid #000;">
              ${contractNo}
            </div>
            <div style="width: 45%; padding: 5px 10px; text-align: right; font-weight: bold; background-color: #e8e8e8; direction: rtl;">
              رقم العقد
            </div>
          </div>
        </div>

      </div>

      <!-- 4. Details Table — alternating rows, no vertical border -->
      <div style="border: 1px solid #000; border-radius: 4px; overflow: hidden; font-size: 11px; margin-bottom: 8px; width: 100%;">
        
        <!-- Row 1: Date (white) -->
        <div style="display: flex; border-bottom: 1px solid #000; direction: ltr; background-color: #ffffff;">
          <div style="width: 30%; padding: 4px 10px; text-align: left; font-weight: bold; font-family: monospace;">
            Date : ${formatDateDDMMYYYY(paymentDate)}
          </div>
          <div style="width: 70%; padding: 4px 10px; text-align: right; font-weight: bold; direction: rtl;">
            التاريخ : ${formatDateYYYYMMDD(paymentDate)}
          </div>
        </div>

        <!-- Row 2: Received From (gray) -->
        <div style="display: flex; border-bottom: 1px solid #000; direction: ltr; background-color: #f0f0f0;">
          <div style="width: 30%; padding: 4px 10px; text-align: left; font-weight: bold;">
            Received From :
          </div>
          <div style="width: 70%; padding: 4px 10px; text-align: right; font-weight: bold; direction: rtl;">
            وصلنا من السيد : ${invoice.tenantName}${invoice.tenantCivilId ? ` - الرقم المدني/سجل تجاري: ${invoice.tenantCivilId}` : ''}
          </div>
        </div>

        <!-- Row 3: The Sum of (white) -->
        <div style="display: flex; border-bottom: 1px solid #000; direction: ltr; background-color: #ffffff;">
          <div style="width: 30%; padding: 4px 10px; text-align: left; font-weight: bold;">
            The Sum of :
          </div>
          <div style="width: 70%; padding: 4px 10px; text-align: right; font-weight: bold; direction: rtl;">
            مبلغ وقدره : ${amountWordsAr}
          </div>
        </div>

        <!-- Row 4: On Account of (gray) -->
        <div style="display: flex; border-bottom: 1px solid #000; direction: ltr; background-color: #f0f0f0;">
          <div style="width: 30%; padding: 4px 10px; text-align: left; font-weight: bold;">
            On Account Of Month :
          </div>
          <div style="width: 70%; padding: 4px 10px; text-align: right; font-weight: bold; direction: rtl;">
            وذلك عن : إيجار الفترة من ${monthPeriod.start} إلى ${monthPeriod.end}
          </div>
        </div>

        <!-- Row 5: Address (white) -->
        <div style="display: flex; border-bottom: 1px solid #000; direction: ltr; background-color: #ffffff;">
          <div style="width: 30%; padding: 4px 10px; text-align: left; font-weight: bold;">
            Address :
          </div>
          <div style="width: 70%; padding: 4px 10px; text-align: right; font-weight: bold; direction: rtl;">
            العنوان : ${invoice.propertyName}
          </div>
        </div>

        <!-- Row 6: Unit (gray) -->
        <div style="display: flex; ${serviceChargeVal > 0 || discount > 0 ? 'border-bottom: 1px solid #000;' : ''} direction: ltr; background-color: #f0f0f0;">
          <div style="width: 30%; padding: 4px 10px; text-align: left; font-weight: bold;">
            Unit :
          </div>
          <div style="width: 70%; padding: 4px 10px; text-align: right; font-weight: bold; direction: rtl;">
            الوحدات : وحدة رقم ${invoice.unitNumber}
          </div>
        </div>

        <!-- Row 7: Service Charge (white) — conditional -->
        ${serviceChargeVal > 0 ? `
        <div style="display: flex; ${discount > 0 ? 'border-bottom: 1px solid #000;' : ''} direction: ltr; background-color: #ffffff;">
          <div style="width: 30%; padding: 4px 10px; text-align: left; font-weight: bold;">
            Service Charge :
          </div>
          <div style="width: 70%; padding: 4px 10px; text-align: right; font-weight: bold; direction: rtl;">
            رسوم خدمات إضافية : ${serviceChargeVal.toFixed(3)} د.ك
          </div>
        </div>
        ` : ''}

        <!-- Row 8: Discount (gray) — conditional -->
        ${discount > 0 ? `
        <div style="display: flex; direction: ltr; background-color: #f0f0f0;">
          <div style="width: 30%; padding: 4px 10px; text-align: left; font-weight: bold;">
            Discount :
          </div>
          <div style="width: 70%; padding: 7px 10px; text-align: right; font-weight: bold; direction: rtl; color: #b91c1c;">
            الخصم الممنوح : ${discount.toFixed(3)} د.ك &nbsp;(القيمة الدفترية: ${bookValue.toFixed(3)} د.ك)
          </div>
        </div>
        ` : ''}

      </div>

      <!-- 5. Signatures -->
      <div style="display: flex; justify-content: space-around; padding: 8px 20px 4px 20px; font-size: 11px; font-weight: bold; color: #000; direction: ltr; margin-top: 4px;">
        <div style="text-align: center;">
          <div>Receiver / المستلم</div>
          <div style="height: 35px; border-bottom: 1px solid #555; min-width: 130px; margin-top: 8px;"></div>
        </div>
        <div style="text-align: center;">
          <div>Accountant / المحاسب</div>
          <div style="height: 35px; border-bottom: 1px solid #555; min-width: 130px; margin-top: 8px;"></div>
        </div>
      </div>

      <!-- 6. Validity Notice -->
      <div style="text-align: center; font-size: 9px; font-weight: bold; margin-top: 6px; color: #000; border-top: 1px solid #000; padding-top: 4px;">
        لا يعتد بهذا الإيصال إلا إذا كان موقعاً ومختوماً من الشركة
      </div>

      <!-- 7. Footer metadata -->
      <div style="display: flex; justify-content: space-between; align-items: center; font-size: 8.5px; color: #333; font-family: monospace; margin-top: 6px; padding-top: 4px; border-top: 1px dashed #000; direction: ltr;">
        <div>printed : ${formatDateWithTime(new Date())}</div>
        <div style="font-weight: bold;">أنظمه شركة Flyvoo برنامج Flyrent</div>
        <div>${copyLabel} | Page 1 of 1</div>
      </div>

    </div>
  `;
};

// قالب سند الصرف والقبض المخصص للحسابات العامة والمصروفات والإيرادات
export const getTransactionReceiptHtml = (
  transaction: Transaction,
  companyInfo: CompanyInfo | null
): string => {
  const amount = Number(transaction.amount || 0);
  const amountWordsAr = numberToArabicWords(amount);
  const amountWordsEn = `${amount.toFixed(3)} Kuwaiti Dinars`;

  const isIncome = transaction.type === 'Income';
  const voucherTitleAr = isIncome ? 'سند قبض نقدي' : 'سند صرف نقدي';
  const voucherTitleEn = isIncome ? 'RECEIPT VOUCHER' : 'PAYMENT VOUCHER';

  const voucherNo = String(transaction.id).substring(0, 8).toUpperCase();
  const paymentDate = formatDate(transaction.date);

  return `
    <div class="print-wrapper" style="border: 2px solid #0f172a; padding: 24px; background: white; direction: rtl; font-family: 'Inter', 'Noto Sans AR', sans-serif;">
      
      <!-- 1. Header (Company details & Logo) -->
      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; align-items: center; border-bottom: 2px solid #0f172a; padding-bottom: 12px; margin-bottom: 20px;">
        <!-- Left (English details) -->
        <div style="text-align: left; font-size: 10px; color: #334155; font-family: monospace; line-height: 1.4;" dir="ltr">
          <div style="font-weight: 900; font-size: 12px; color: #0f172a; margin-bottom: 2px;">${companyInfo?.name_en || 'FLY RENT SYSTEM'}</div>
          <div>C.R. No: ${companyInfo?.cr_number || '---'}</div>
          <div>Tel / Fax: ${companyInfo?.phone || '---'} ${companyInfo?.fax ? `/ ${companyInfo?.fax}` : ''}</div>
          <div style="text-transform: lowercase;">Email: ${companyInfo?.email || '---'}</div>
        </div>

        <!-- Center (Logo) -->
        <div style="display: flex; justify-content: center; align-items: center;">
          ${companyInfo?.logo_url ? `
            <img src="${companyInfo.logo_url}" alt="Logo" style="max-height: 70px; max-width: 100%; object-fit: contain;" />
          ` : `
            <div style="border: 1px solid #cbd5e1; padding: 8px; border-radius: 6px; color: #94a3b8; font-size: 11px; font-weight: bold; text-align: center;">LOGO</div>
          `}
        </div>

        <!-- Right (Arabic details) -->
        <div style="text-align: right; font-size: 10px; color: #334155; line-height: 1.4; font-weight: bold;">
          <div style="font-weight: 900; font-size: 12px; color: #0f172a; margin-bottom: 2px;">${companyInfo?.name_ar || 'نظام فلاي رينت لإدارة العقارات'}</div>
          <div>سجل تجاري: ${companyInfo?.cr_number || '---'}</div>
          <div>تليفون / فاكس: ${companyInfo?.phone || '---'} ${companyInfo?.fax ? `/ ${companyInfo?.fax}` : ''}</div>
          <div>البريد الإلكتروني: ${companyInfo?.email || '---'}</div>
        </div>
      </div>

      <!-- 2. Voucher Header Title Banner -->
      <div style="text-align: center; margin-bottom: 24px; border-bottom: 1px solid #cbd5e1; padding-bottom: 8px;">
        <h2 style="font-size: 18px; font-weight: 900; color: #0f172a; margin: 0;">${voucherTitleAr}</h2>
        <h3 style="font-size: 10px; font-weight: 900; color: #64748b; font-family: monospace; text-transform: uppercase; margin: 2px 0 0 0; letter-spacing: 2px;">${voucherTitleEn}</h3>
      </div>

      <!-- 3. Voucher Info (No & Date) -->
      <div style="display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 12px; font-weight: bold;">
        <div>
          <span>رقم السند / Voucher No: </span>
          <span style="font-family: monospace; color: #0f172a; border-bottom: 1px solid #94a3b8; padding: 0 8px;">${voucherNo}</span>
        </div>
        <div>
          <span>التاريخ / Date: </span>
          <span style="font-family: monospace; color: #0f172a; border-bottom: 1px solid #94a3b8; padding: 0 8px;">${paymentDate}</span>
        </div>
      </div>

      <!-- 4. Main Voucher Fields (Dotted Lines Style for accounting vouchers) -->
      <div style="font-size: 12px; line-height: 2.2; margin-bottom: 24px;">
        
        <!-- Recipient / Payer -->
        <div style="display: flex; align-items: baseline;">
          <div style="width: 150px; font-weight: bold; color: #475569;">
            ${isIncome ? 'استلمنا من السيد/الجهة' : 'صرفنا إلى السيد/الجهة'}:
          </div>
          <div style="flex-grow: 1; border-bottom: 1px dashed #cbd5e1; padding-right: 8px; font-weight: 900; color: #0f172a; font-size: 13px;">
            ${transaction.tenantName || (transaction.description?.includes('إيجار محصل من') ? transaction.description.match(/إيجار محصل من (.*?) عن/)?.[1] : null) || transaction.description?.split(' - ')?.[0] || (isIncome ? 'إيرادات عامة' : 'مصروفات تشغيلية')}
          </div>
        </div>

        <!-- Amount in Arabic & Numeric -->
        <div style="display: flex; align-items: baseline;">
          <div style="width: 150px; font-weight: bold; color: #475569;">مبلغ وقدره / The Sum of:</div>
          <div style="flex-grow: 1; border-bottom: 1px dashed #cbd5e1; padding-right: 8px; font-weight: 900; color: #0f172a;">
            ${amountWordsAr}
          </div>
          <div style="width: 120px; font-weight: 900; color: #020617; font-family: monospace; font-size: 14px; text-align: left; background-color: #f1f5f9; padding: 0 10px; border-radius: 4px; border: 1px solid #e2e8f0; line-height: 1.8;">
            ${amount.toFixed(3)} د.ك.
          </div>
        </div>

        <!-- Amount in English Words -->
        <div style="display: flex; align-items: baseline;" dir="ltr">
          <div style="width: 150px; font-weight: bold; color: #64748b; font-family: monospace; text-align: left;">In Words:</div>
          <div style="flex-grow: 1; border-bottom: 1px dashed #cbd5e1; padding-left: 8px; color: #475569; font-family: monospace; font-size: 11px;">
            ${amountWordsEn}
          </div>
        </div>

        <!-- On Account of (Statement) -->
        <div style="display: flex; align-items: baseline;">
          <div style="width: 150px; font-weight: bold; color: #475569;">وذلك عن (البيان) / For:</div>
          <div style="flex-grow: 1; border-bottom: 1px dashed #cbd5e1; padding-right: 8px; font-weight: bold; color: #334155;">
            ${transaction.description || 'بدون تفاصيل إضافية'}
          </div>
        </div>

        <!-- Account Category & Property Link -->
        <div style="display: flex; align-items: baseline;">
          <div style="width: 150px; font-weight: bold; color: #475569;">التصنيف والعقار:</div>
          <div style="flex-grow: 1; border-bottom: 1px dashed #cbd5e1; padding-right: 8px; font-weight: bold; color: #475569;">
            <span>[ تصنيف: ${transaction.categoryName || 'مصاريف عامة'} ]</span>
            <span style="margin: 0 12px; color: #94a3b8;">|</span>
            <span>عقار: ${transaction.propertyName || 'عام / غير محدد'}</span>
          </div>
        </div>
      </div>

      <!-- 5. Signatures (Three signature lines for corporate checks) -->
      <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; border-top: 1px solid #cbd5e1; padding-top: 16px; margin-top: 32px; font-size: 11px; text-align: center; color: #475569; font-weight: bold;">
        <div>
          <div>المستلم / Receiver</div>
          <div style="height: 45px; border-bottom: 1px dashed #94a3b8; width: 80%; margin: 8px auto 0 auto;"></div>
        </div>
        <div>
          <div>المحاسب / Accountant</div>
          <div style="height: 45px; border-bottom: 1px dashed #94a3b8; width: 80%; margin: 8px auto 0 auto;"></div>
        </div>
        <div>
          <div>المدير العام / General Manager</div>
          <div style="height: 45px; border-bottom: 1px dashed #94a3b8; width: 80%; margin: 8px auto 0 auto;"></div>
        </div>
      </div>

      <!-- 6. Validity Notice -->
      <div style="text-align: center; font-size: 9px; color: #64748b; font-weight: bold; border-top: 1px solid #f1f5f9; padding-top: 10px; margin-top: 24px;">
        لا يعتد بهذا السند إلا إذا كان موقعاً ومختوماً من الشركة
        <div style="font-size: 8px; color: #94a3b8; font-family: monospace; margin-top: 1px;" dir="ltr">
          This voucher is not valid unless signed and stamped by the company.
        </div>
      </div>

      <!-- 7. Footer metadata -->
      <div style="display: flex; justify-content: space-between; align-items: center; font-size: 8px; color: #94a3b8; font-family: monospace; margin-top: 20px; padding-top: 6px; border-top: 1px solid #f1f5f9;">
        <div>Printed: ${new Date().toLocaleString('en-GB')}</div>
        <div style="font-weight: bold; text-transform: uppercase; color: #cbd5e1;">Financial Copy / النسخة المالية</div>
        <div>Page 1 of 1</div>
      </div>

    </div>
  `;
};
